/**
 * Configuration for hiding hints as users progress through levels.
 * Each hint type is mapped to the level number at which it should be hidden.
 * Once a user passes that level, the hint will be hidden.
 */
export const LEVELING = {
  // Hide the apple ring (budget visualization) when not selected after level 3
  HIDE_APPLE_RING: 0,

  // Hide the heart sprite (surplus indicator) after level 5
  HIDE_HEART_SPRITE: 1000,

  // Hide the "needs" kibbles count after level 7
  HIDE_NEEDS_KIBBLES: 0,

  // Hide the budget info labels after level 10
  HIDE_BUDGET_LABELS: 0,

  // Hide hover tooltips after level 12
  HIDE_HOVER_TOOLTIPS: 1000,
} as const;

export type HintType = keyof typeof LEVELING;

/**
 * Check if a specific hint should be hidden based on the user's current level
 * @param hintType The type of hint to check
 * @param userLevel The user's current level (from stats)
 * @returns true if the hint should be hidden, false if it should be shown
 */
export function shouldHideHint(
  hintType: HintType,
  userLevel: number | null,
): boolean {
  if (userLevel === null) {
    // If we don't know the user's level, show all hints
    return false;
  }

  const hideAtLevel = LEVELING[hintType];
  return userLevel >= hideAtLevel;
}

/**
 * Get all hint visibility states for the current user level
 * @param userLevel The user's current level
 * @returns An object with boolean values for each hint type
 */
export function getHintVisibility(userLevel: number | null) {
  return {
    showAppleRing: !shouldHideHint("HIDE_APPLE_RING", userLevel),
    showHeartSprite: !shouldHideHint("HIDE_HEART_SPRITE", userLevel),
    showNeedsKibbles: !shouldHideHint("HIDE_NEEDS_KIBBLES", userLevel),
    showBudgetLabels: !shouldHideHint("HIDE_BUDGET_LABELS", userLevel),
    showHoverTooltips: !shouldHideHint("HIDE_HOVER_TOOLTIPS", userLevel),
    // "Has" kibbles should always be shown
    showHasKibbles: true,
  };
}
